/**
 * Detect / normalize LEAP RFID encodings for document-wizard allocation.
 * Mirrors engine normalizeRfidInput (no Buffer — RN-safe).
 */

const PREFIX = 'LEAP'
const RFID_LENGTH = 20
const LEAP_HEX_PREFIX = '4C454150'
const SCANNER_HEX_LENGTH = 24
const ASCII_HEX_LENGTH = 40

function hexToAscii(hex: string): string | null {
  if (hex.length % 2 !== 0 || !/^[0-9A-F]+$/i.test(hex)) return null
  let out = ''
  for (let i = 0; i < hex.length; i += 2) {
    const code = Number.parseInt(hex.slice(i, i + 2), 16)
    if (!Number.isFinite(code) || code < 0x20 || code > 0x7e) return null
    out += String.fromCharCode(code)
  }
  return out.toUpperCase()
}

/** Normalize scanner / pasted input toward canonical LEAP when possible. */
export function normalizeLeapInput(raw: string): string {
  const cleaned = (raw ?? '').trim().toUpperCase().replace(/[\s:.-]/g, '')
  if (!cleaned) return ''

  if (cleaned.startsWith(PREFIX)) {
    return cleaned
  }

  if (!/^[0-9A-F]+$/.test(cleaned) || cleaned.length % 2 !== 0) {
    return cleaned
  }

  if (cleaned.startsWith(LEAP_HEX_PREFIX) && cleaned.length === SCANNER_HEX_LENGTH) {
    return `${PREFIX}${cleaned.slice(LEAP_HEX_PREFIX.length)}`
  }

  if (cleaned.length === ASCII_HEX_LENGTH) {
    const decoded = hexToAscii(cleaned)
    if (decoded?.startsWith(PREFIX)) return decoded
  }

  if (cleaned.startsWith(LEAP_HEX_PREFIX) && cleaned.length > LEAP_HEX_PREFIX.length) {
    return `${PREFIX}${cleaned.slice(LEAP_HEX_PREFIX.length)}`
  }

  return cleaned
}

/**
 * True when the tag is (or normalizes to) a LEAP EPC.
 * Hex forms that decode to LEAP… also count so scanners hit the engine path.
 */
export function isLeapTag(raw: string): boolean {
  const normalized = normalizeLeapInput(raw)
  if (!normalized) return false
  if (normalized.startsWith(PREFIX) && normalized.length === RFID_LENGTH) return true
  // Still-hex but clearly LEAP-prefixed EPC (engine will normalize)
  const cleaned = (raw ?? '').trim().toUpperCase().replace(/[\s:.-]/g, '')
  return cleaned.startsWith(LEAP_HEX_PREFIX) && cleaned.length >= SCANNER_HEX_LENGTH
}

/** Canonical key for tagSet / skuByTag (LEAP form when detectable). */
export function canonicalTagId(raw: string): string {
  const leap = normalizeLeapInput(raw)
  if (leap.startsWith(PREFIX) && leap.length === RFID_LENGTH) return leap
  return (raw ?? '').trim().toUpperCase()
}
